'use client';
import React from 'react';
import Image from 'next/image';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Link from 'next/link';

const values = [
  {
    icon: '⚡',
    title: 'Speed first',
    desc: 'Every second counts when hunger strikes. We obsess over delivery times so you don\'t have to wait.',
    accent: '#f97316',
    bg: 'linear-gradient(135deg, #1a0e00 0%, #0d0700 100%)',
    border: '#2d1a00',
  },
  {
    icon: '🤝',
    title: 'Community driven',
    desc: 'We lift local vendors, empower riders with fair pay, and give customers the best of their neighbourhood.',
    accent: '#22c55e',
    bg: '#111111',
    border: '#1a3a1a',
  },
  {
    icon: '🔒',
    title: 'Trust & safety',
    desc: 'Secure payments, verified vendors, and real-time tracking — because your peace of mind matters.',
    accent: '#38bdf8',
    bg: '#111111',
    border: '#0c2a3a',
  },
  {
    icon: '🌍',
    title: 'Built for Africa',
    desc: 'Designed from the ground up for African cities — our infrastructure, pricing, and UX reflect local realities.',
    accent: '#f5c518',
    bg: '#111111',
    border: '#2d2700',
  },
];

const milestones = [
  { year: '2023', event: 'mdeliva founded under EnterpriseT56 with a vision to fix last-mile delivery.' },
  { year: '2024', event: 'Onboarded 1,200+ local vendors across multiple cities.' },
  { year: '2025', event: 'Launched fleet management portal and rider rewards programme.' },
  { year: '2026', event: 'Expanding delivery coverage and rolling out the public app.' },
];

export default function AboutPage() {
  return (
    <main className="min-h-screen bg-[#0a0a0a] text-white overflow-x-hidden">
      <Navbar />
      {/* Hero */}
      <section className="relative pt-32 pb-16 px-4 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: 'linear-gradient(rgba(249,115,22,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(249,115,22,0.03) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }} />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[700px] h-[350px] bg-[#f97316]/6 blur-[130px] rounded-full pointer-events-none" />

        {/* Floating dots */}
        <div className="absolute top-36 left-16 w-2 h-2 rounded-full bg-[#f97316]/50 animate-pulse pointer-events-none" />
        <div className="absolute top-52 right-24 w-1.5 h-1.5 rounded-full bg-[#f5c518]/60 animate-pulse pointer-events-none" style={{ animationDelay: '0.7s' }} />
        <div className="absolute top-44 left-1/3 w-1 h-1 rounded-full bg-[#f97316]/40 animate-pulse pointer-events-none" style={{ animationDelay: '1.1s' }} />

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1a0e00] border border-[#3d2000] text-[#f97316] text-sm font-medium mb-6">
            <span>🏢</span>
            <span>Our story</span>
          </span>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Delivering more than{' '}
            <span style={{
              background: 'linear-gradient(135deg, #f97316, #f5c518)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontStyle: 'italic',
            }}>
              just food.
            </span>
          </h1>
          <p className="text-gray-400 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
            mdeliva is on a mission to make fast, reliable delivery accessible to everyone — customers, vendors, and riders alike.
          </p>
        </div>
      </section>
      {/* Logo + Brand */}
      <section className="py-10 px-4">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-10">
          <div className="flex-shrink-0 relative">
            <div className="w-40 h-40 rounded-3xl overflow-hidden border border-[#2d1a00] shadow-2xl shadow-[#f97316]/10"
              style={{ background: 'linear-gradient(135deg, #1a0e00, #0d0700)' }}>
              <Image
                src="/assets/images/logo_cropped_512x512-1781773297617.png"
                alt="mdeliva logo — fast food and grocery delivery service"
                width={160}
                height={160}
                className="w-full h-full object-contain p-4"
              />
            </div>
            <div className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-[#f97316] flex items-center justify-center text-sm shadow-lg">
              🛵
            </div>
          </div>
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
              We are mdeliva
            </h2>
            <p className="text-gray-400 text-base leading-relaxed mb-4">
              Born out of a frustration with slow, unreliable delivery services, mdeliva was built to change the game. We connect hungry customers with local vendors and empower riders to earn on their own schedule — all through a platform designed for speed, simplicity, and scale.
            </p>
            <p className="text-gray-400 text-base leading-relaxed">
              mdeliva is a product of{' '}
              <a href="https://enterpriset56.com" target="_blank" rel="noopener noreferrer" className="text-[#f97316] hover:text-[#f5c518] transition-colors duration-200 font-semibold">
                EnterpriseT56
              </a>
              , a technology company building solutions for the African market and beyond.
            </p>
          </div>
        </div>
      </section>
      {/* Values */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest bg-[#0a1a0a] border border-[#1a3a1a] text-[#22c55e]">
              What we stand for
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-10">
            Our values
          </h2>

          {/* Bento grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Wide card */}
            <div
              className="md:col-span-2 rounded-2xl p-7 border"
              style={{ background: values?.[0]?.bg, borderColor: values?.[0]?.border }}
            >
              <div className="text-3xl mb-3">{values?.[0]?.icon}</div>
              <h3 className="text-white font-bold text-xl mb-2">{values?.[0]?.title}</h3>
              <p className="text-gray-400 text-sm max-w-lg leading-relaxed">{values?.[0]?.desc}</p>
              <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium"
                style={{ color: values?.[0]?.accent, backgroundColor: '#1a0e00', border: `1px solid ${values?.[0]?.border}` }}>
                🚴 Avg. delivery: 18 minutes
              </div>
            </div>

            {values?.slice(1)?.map((v) => (
              <div
                key={v?.title}
                className="rounded-2xl p-6 border"
                style={{ background: v?.bg, borderColor: v?.border }}
              >
                <div className="text-3xl mb-3">{v?.icon}</div>
                <h3 className="text-white font-bold text-base mb-2">{v?.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{v?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Timeline */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest bg-[#1a0e00] border border-[#3d2000] text-[#f97316]">
              Our journey
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-10">
            How we got here
          </h2>

          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-[22px] top-0 bottom-0 w-px bg-gradient-to-b from-[#f97316]/60 via-[#f5c518]/40 to-transparent" />

            <div className="space-y-8">
              {milestones?.map((m, i) => (
                <div key={m?.year} className="flex items-start gap-6 pl-2">
                  <div
                    className="flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center text-xs font-bold border-2 z-10"
                    style={{
                      background: i === milestones?.length - 1 ? 'linear-gradient(135deg, #f97316, #f5c518)' : '#111111',
                      borderColor: i === milestones?.length - 1 ? '#f97316' : '#2d2d2d',
                      color: i === milestones?.length - 1 ? '#000' : '#f97316',
                    }}
                  >
                    {m?.year?.slice(2)}
                  </div>
                  <div className="bg-[#111111] border border-[#1e1e1e] rounded-xl px-5 py-4 flex-1">
                    <p className="text-[#f97316] text-xs font-bold uppercase tracking-widest mb-1">{m?.year}</p>
                    <p className="text-gray-300 text-sm leading-relaxed">{m?.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      {/* CTA */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="bg-[#111111] border border-[#1e1e1e] rounded-2xl p-10 text-center relative overflow-hidden">
            <div className="absolute inset-0 pointer-events-none" style={{
              background: 'radial-gradient(ellipse at 50% 0%, rgba(249,115,22,0.08) 0%, transparent 70%)'
            }} />
            <div className="relative z-10">
              <div className="text-4xl mb-4">🚀</div>
              <h3 className="text-white font-bold text-2xl md:text-3xl mb-3">
                Join the mdeliva family
              </h3>
              <p className="text-gray-400 text-base mb-8 max-w-md mx-auto">
                Whether you&apos;re a customer, vendor, or rider — there&apos;s a place for you in our growing community.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <Link
                  href="/contact"
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-semibold text-black text-base transition-all duration-200 hover:scale-105 hover:brightness-110"
                  style={{ background: 'linear-gradient(135deg, #f5c518, #f97316)' }}
                >
                  <span>✉️</span>
                  <span>Get in touch</span>
                </Link>
                <Link
                  href="/faq"
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-medium text-white text-base bg-[#1e1e1e] border border-[#333] hover:bg-[#2a2a2a] transition-all duration-200"
                >
                  Read FAQ
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
}
